import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./Attributes.module.scss";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import { useAuth } from "../../context/AuthContext";
import Modal from "../../components/common/modal/Modal";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import PlusIcon from "../../assets/images/actions/PlusIcon";
import AttributesService from "../../api/services/attributes.service";
import CategorisService from "../../api/services/categories.service";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import NestedSelect from "../../components/common/nested-select/NestedSelect";
import { CategoriesTypes } from "../../api/types/products.types";
import Plus from "../../assets/images/svgs/Plus";
import Minus from "../../assets/images/svgs/Minus";
import SwitchButton from "../../components/common/switch-button/SwitchButton";

interface Errors {
  name_en: string;
  name_az: string;
  name_es: string;
  name_de: string;
  category_id: string;
}

type Language = "en" | "az" | "es" | "de";

const Attributes = () => {
  const { t } = useTranslation("translation");

  const columns: Column[] = [
    { key: "name", header: t("attributes.table.attribute_name") as string },
    {
      key: "category",
      header: t("attributes.table.category_name") as string,
      render: (c) => c?.name,
    },
    {
      key: "options_api",
      header: t("attributes.table.options_api") as string,
      render: (o) => o || "-",
    },
    {
      key: "values",
      header: t("attributes.table.values") as string,
      render: (v) => (
        <div>
          {v?.map((el: any, idx: number) => (
            <p key={idx}>{el?.name}</p>
          ))}
        </div>
      ),
    },
  ];

  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [originalData, setOriginalData] = useState<any>(null);
  const [activeLanguage, setActiveLanguage] = useState<Language>("en");

  const [formData, setFormData] = useState<{
    name_en: string;
    name_az: string;
    name_es: string;
    name_de: string;
    is_filter: boolean;
    is_view: boolean;
    category_id: number | null;
    options_api?: string;
    values_en: { id?: number; name: string }[];
    values_az: { id?: number; name: string }[];
    values_es: { id?: number; name: string }[];
    values_de: { id?: number; name: string }[];
  }>({
    name_en: "",
    name_az: "",
    name_es: "",
    name_de: "",
    is_filter: false,
    is_view: false,
    category_id: null,
    options_api: "",
    values_en: [{ name: "" }],
    values_az: [{ name: "" }],
    values_es: [{ name: "" }],
    values_de: [{ name: "" }],
  });

  const { setButtons, searchValue } = useAuth();
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [errors, setErrors] = useState<Partial<Errors>>({});

  const { data: categories, loaderRef } = useInfiniteScroll<CategoriesTypes>({
    fetchFn: async (page) => {
      const res = await CategorisService.getAllCategories();
      return res.data;
    },
    pageSize: 20,
  });

  const languages = [
    { code: "en" as Language, label: "English", flag: "🇬🇧" },
    { code: "az" as Language, label: "Azərbaycanca", flag: "🇦🇿" },
    { code: "es" as Language, label: "Español", flag: "🇪🇸" },
    { code: "de" as Language, label: "Deutsch", flag: "🇩🇪" },
  ];

  const getData = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await AttributesService.getAllData(page, limit, searchValue);
      setAttributes(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      await AttributesService.deleteData(id);
      setDeleteModal(false);
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Errors> = {};
    if (!formData.name_en?.trim())
      newErrors.name_en = t(
        "attributes.errors.english_name_required",
      ) as string;
    if (!formData.category_id)
      newErrors.category_id = t(
        "attributes.errors.category_required",
      ) as string;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to filter out empty values and prepare for backend
  const filterValues = (values: { id?: number; name: string }[]) => {
    return values
      .filter((v) => v.name.trim() !== "")
      .map((v) => ({ name: v.name.trim() }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const data = new FormData();

      // Prepare clean values for all languages
      const cleanValuesEn = filterValues(formData.values_en);
      const cleanValuesAz = filterValues(formData.values_az);
      const cleanValuesEs = filterValues(formData.values_es);
      const cleanValuesDe = filterValues(formData.values_de);

      if (editId) {
        // Update mode - send all fields (backend expects full data)
        data.append("name_en", formData.name_en);
        data.append("name_az", formData.name_az);
        data.append("name_es", formData.name_es);
        data.append("name_de", formData.name_de);
        data.append("is_filter", String(formData.is_filter));
        data.append("is_view", String(formData.is_view));
        data.append("category_id", String(formData.category_id));
        data.append("type", "2");
        data.append("options_api", formData.options_api || "");

        // Always send values for all languages as JSON string
        data.append("values_en", JSON.stringify(cleanValuesEn));
        data.append("values_az", JSON.stringify(cleanValuesAz));
        data.append("values_es", JSON.stringify(cleanValuesEs));
        data.append("values_de", JSON.stringify(cleanValuesDe));

        await AttributesService.updateData(editId, data);
      } else {
        // Create mode
        data.append("name_en", formData.name_en);
        if (formData.name_az) data.append("name_az", formData.name_az);
        if (formData.name_es) data.append("name_es", formData.name_es);
        if (formData.name_de) data.append("name_de", formData.name_de);
        data.append("is_filter", String(formData.is_filter));
        data.append("is_view", String(formData.is_view));
        data.append("category_id", String(formData.category_id));
        data.append("type", "2");

        if (formData.options_api?.trim())
          data.append("options_api", formData.options_api);

        // Send values for each language as JSON string
        if (cleanValuesEn.length > 0)
          data.append("values_en", JSON.stringify(cleanValuesEn));
        if (cleanValuesAz.length > 0)
          data.append("values_az", JSON.stringify(cleanValuesAz));
        if (cleanValuesEs.length > 0)
          data.append("values_es", JSON.stringify(cleanValuesEs));
        if (cleanValuesDe.length > 0)
          data.append("values_de", JSON.stringify(cleanValuesDe));

        await AttributesService.createOne(data);
      }

      setIsVisible(false);
      setEditId(null);
      setOriginalData(null);
      setActiveLanguage("en");
      setFormData({
        name_en: "",
        name_az: "",
        name_es: "",
        name_de: "",
        category_id: null,
        options_api: "",
        values_en: [{ name: "" }],
        values_az: [{ name: "" }],
        values_es: [{ name: "" }],
        values_de: [{ name: "" }],
        is_filter: false,
        is_view: false,
      });
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  // Helper function to parse values from backend
  const parseValues = (values: any) => {
    if (!values) return [{ name: "" }];

    // If it's a string, try to parse it as JSON
    if (typeof values === "string") {
      try {
        const parsed = JSON.parse(values);
        return Array.isArray(parsed) && parsed.length > 0
          ? parsed
          : [{ name: "" }];
      } catch {
        return [{ name: "" }];
      }
    }

    // If it's already an array
    if (Array.isArray(values)) {
      return values.length > 0 ? values : [{ name: "" }];
    }

    return [{ name: "" }];
  };

  const handleEdit = async (id: number) => {
    setErrors({});
    try {
      const res = await AttributesService.getById(id);
      const data = res.data;

      console.log("Edit data:", data);

      setEditId(id);

      const parsedData = {
        name_en: data.name_en || data.name || "",
        name_az: data.name_az || "",
        name_es: data.name_es || "",
        name_de: data.name_de || "",
        category_id: data.category_id,
        options_api: data.options_api || "",
        values_en: parseValues(data.values_en),
        values_az: parseValues(data.values_az),
        values_es: parseValues(data.values_es),
        values_de: parseValues(data.values_de),
        is_filter: data.is_filter,
        is_view: data.is_view,
      };

      setOriginalData(parsedData);
      setFormData(parsedData);

      setActiveLanguage("en");
      setIsVisible(true);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    setButtons([
      <Button
        icon={<PlusIcon />}
        key="add"
        onClick={() => {
          setIsVisible(true);
          setEditId(null);
          setOriginalData(null);
          setActiveLanguage("en");
          setErrors({});
          setFormData({
            name_en: "",
            name_az: "",
            name_es: "",
            name_de: "",
            category_id: null,
            options_api: "",
            values_en: [{ name: "" }],
            values_az: [{ name: "" }],
            values_es: [{ name: "" }],
            values_de: [{ name: "" }],
            is_filter: false,
            is_view: false,
          });
        }}
      >
        {t("attributes.add_button") as string}
      </Button>,
    ]);
    return () => setButtons([]);
  }, [setButtons]);

  const renderLanguageTabs = () => (
    <div className={styles.languageTabs}>
      {languages.map((lang) => (
        <button
          key={lang.code}
          type="button"
          className={`${styles.languageTab} ${
            activeLanguage === lang.code ? styles.active : ""
          }`}
          onClick={() => setActiveLanguage(lang.code)}
        >
          <span className={styles.flag}>{lang.flag}</span>
          <span className={styles.langLabel}>{lang.label}</span>
          {lang.code === "en" && <span className={styles.required}>*</span>}
        </button>
      ))}
    </div>
  );

  const getCurrentValues = () => {
    switch (activeLanguage) {
      case "az":
        return formData.values_az;
      case "es":
        return formData.values_es;
      case "de":
        return formData.values_de;
      default:
        return formData.values_en;
    }
  };

  const updateCurrentValues = (newValues: { id?: number; name: string }[]) => {
    switch (activeLanguage) {
      case "az":
        setFormData({ ...formData, values_az: newValues });
        break;
      case "es":
        setFormData({ ...formData, values_es: newValues });
        break;
      case "de":
        setFormData({ ...formData, values_de: newValues });
        break;
      default:
        setFormData({ ...formData, values_en: newValues });
    }
  };

  return (
    <div className={styles.delivery}>
      <TitleUpdater title={t("attributes.title") as string} />
      <DataTable
        table_title={t("attributes.title") as string}
        data={attributes}
        columns={columns}
        actions
        onDelete={(id) => {
          setDeleteModal(true);
          setDeleteId(id);
        }}
        onEdit={(id) => handleEdit(id)}
        other
        loading={loading}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("attributes.delete_title") as string}
      />
      <Modal
        isOpen={isVisible}
        onClose={() => {
          setIsVisible(false);
          setEditId(null);
          setOriginalData(null);
          setActiveLanguage("en");
          setFormData({
            name_en: "",
            name_az: "",
            name_es: "",
            name_de: "",
            category_id: null,
            options_api: "",
            values_en: [{ name: "" }],
            values_az: [{ name: "" }],
            values_es: [{ name: "" }],
            values_de: [{ name: "" }],
            is_filter: false,
            is_view: false,
          });
        }}
        title={
          editId
            ? (t("attributes.edit_title") as string)
            : (t("attributes.add_button") as string)
        }
        onSubmit={handleSubmit}
      >
        <form className={styles.form}>
          {renderLanguageTabs()}

          <div className={styles.languageContent}>
            {activeLanguage === "en" && (
              <Input
                label={t("attributes.form.name_english") as string}
                placeholder={t("attributes.form.placeholder_english") as string}
                value={formData.name_en}
                onChange={(e) => {
                  setFormData({ ...formData, name_en: e.target.value });
                  setErrors((prev) => ({ ...prev, name_en: "" }));
                }}
                error={errors.name_en}
              />
            )}

            {activeLanguage === "az" && (
              <Input
                label={t("attributes.form.name_azerbaijani") as string}
                placeholder={
                  t("attributes.form.placeholder_azerbaijani") as string
                }
                value={formData.name_az}
                onChange={(e) => {
                  setFormData({ ...formData, name_az: e.target.value });
                  setErrors((prev) => ({ ...prev, name_az: "" }));
                }}
                error={errors.name_az}
              />
            )}

            {activeLanguage === "es" && (
              <Input
                label={t("attributes.form.name_spanish") as string}
                placeholder={t("attributes.form.placeholder_spanish") as string}
                value={formData.name_es}
                onChange={(e) => {
                  setFormData({ ...formData, name_es: e.target.value });
                  setErrors((prev) => ({ ...prev, name_es: "" }));
                }}
                error={errors.name_es}
              />
            )}

            {activeLanguage === "de" && (
              <Input
                label={t("attributes.form.name_german") as string}
                placeholder={t("attributes.form.placeholder_german") as string}
                value={formData.name_de}
                onChange={(e) => {
                  setFormData({ ...formData, name_de: e.target.value });
                  setErrors((prev) => ({ ...prev, name_de: "" }));
                }}
                error={errors.name_de}
              />
            )}
          </div>

          <NestedSelect
            label={t("attributes.form.category") as string}
            categories={categories}
            value={formData.category_id || null}
            onChange={(value) => {
              setFormData({ ...formData, category_id: value });
              setErrors((prev) => ({ ...prev, category_id: "" }));
            }}
            error={errors.category_id}
            className={styles.input}
            loaderRef={loaderRef}
          />
          <Input
            label={t("attributes.form.options_api") as string}
            placeholder={t("attributes.form.placeholder_api") as string}
            value={formData.options_api}
            onChange={(e) => {
              const value = e.target.value;
              setFormData({
                ...formData,
                options_api: value,
              });
            }}
          />
          {!formData.options_api && (
            <div className={styles.valuesBlock}>
              <label className={styles.label}>
                {t("attributes.table.values") as string} (
                {languages.find((l) => l.code === activeLanguage)?.label})
              </label>
              {getCurrentValues().map((val, index) => (
                <div key={index} className={styles.valueRow}>
                  <Input
                    placeholder={`${t("attributes.form.placeholder_value") as string} ${index + 1}`}
                    value={val.name}
                    onChange={(e) => {
                      const updatedValues = [...getCurrentValues()];
                      updatedValues[index].name = e.target.value;
                      updateCurrentValues(updatedValues);
                    }}
                  />
                  <div className={styles.valueActions}>
                    {index === getCurrentValues().length - 1 && (
                      <div
                        onClick={() =>
                          updateCurrentValues([
                            ...getCurrentValues(),
                            { name: "" },
                          ])
                        }
                      >
                        <Plus />
                      </div>
                    )}
                    {getCurrentValues().length > 1 && (
                      <div
                        onClick={() => {
                          const updatedValues = getCurrentValues().filter(
                            (_, i) => i !== index,
                          );
                          updateCurrentValues(updatedValues);
                        }}
                      >
                        <Minus />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={styles.switch_btns}>
            <SwitchButton
              label={t("attributes.form.show_in_filter") as string}
              checked={formData.is_filter}
              onChange={(value) => {
                setFormData({ ...formData, is_filter: value });
              }}
            />
            <SwitchButton
              label={t("attributes.form.set_as_changeable") as string}
              checked={formData.is_view}
              onChange={(value) => {
                setFormData({ ...formData, is_view: value });
              }}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Attributes;
